import { ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';

const HeroSection = () => {
  const highlights = [
    'No Upfront Costs',
    'No MOQs',
    'USA Fulfillment',
    'Global Shipping',
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden bg-coastal-offwhite pt-20"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-bl from-coastal-accent/50 to-transparent" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            {/* Badge */}
            <div
              data-testid="hero-badge"
              className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-2 text-sm font-medium text-gray-600 shadow-sm animate-fade-in"
            >
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              Premium POD Fulfillment Partner
            </div>

            {/* Heading */}
            <h1
              data-testid="hero-heading"
              className="font-heading text-5xl md:text-6xl lg:text-7xl tracking-tighter font-bold text-coastal-navy leading-tight animate-slide-up"
            >
              Create and sell{' '}
              <span className="text-coastal-orange">premium</span> custom
              products easily
            </h1>

            {/* Subheading */}
            <p
              data-testid="hero-subheading"
              className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-xl animate-slide-up animation-delay-100"
            >
              Design premium apparel, home goods, and accessories. We handle
              production, quality control, and worldwide shipping directly to
              your customers.
            </p>

            {/* Highlights */}
            <div
              data-testid="hero-highlights"
              className="flex flex-wrap gap-4 animate-slide-up animation-delay-200"
            >
              {highlights.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 text-gray-700"
                >
                  <CheckCircle className="w-5 h-5 text-coastal-orange" />
                  <span className="font-medium">{item}</span>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div
              data-testid="hero-ctas"
              className="flex flex-col sm:flex-row gap-4 animate-slide-up animation-delay-300"
            >
              <Button
                data-testid="hero-cta-primary"
                onClick={(e) => scrollToSection(e, '#contact')}
                className="bg-coastal-orange hover:bg-coastal-orange/90 text-white rounded-full px-8 py-6 text-lg font-semibold transition-all hover:-translate-y-1 shadow-lg hover:shadow-xl flex items-center gap-2"
              >
                Start Designing
                <ArrowRight className="w-5 h-5" />
              </Button>
              <Button
                data-testid="hero-cta-secondary"
                onClick={(e) => scrollToSection(e, '#products')}
                variant="outline"
                className="border-2 border-coastal-navy text-coastal-navy hover:bg-coastal-navy hover:text-white rounded-full px-8 py-6 text-lg font-semibold transition-all hover:-translate-y-1"
              >
                View Catalog
              </Button>
            </div>

          </div>

          {/* Right Visual */}
          <div
            data-testid="hero-visual"
            className="relative lg:pl-8 animate-fade-in"
          >
            <div className="relative">
              {/* Main Image */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/3046f3c9-fc92-42ea-9d04-de8d41abd2a6/images/7c86c6885c3dfb74a304363105d2dd365af4cfb3155c7d62f24a54c17195683c.png"
                  alt="POD Fulfillment"
                  className="w-full h-auto object-cover"
                />
              </div>

              {/* Floating Card 1 */}
              <div className="absolute -left-4 top-1/4 bg-white rounded-2xl p-4 shadow-xl border border-gray-100 animate-fade-in animation-delay-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-coastal-navy">
                      Order Shipped
                    </p>
                    <p className="text-sm text-gray-500">2 min ago</p>
                  </div>
                </div>
              </div>

              {/* Floating Card 2 */}
              <div className="absolute -right-4 bottom-1/4 bg-white rounded-2xl p-4 shadow-xl border border-gray-100 animate-fade-in animation-delay-300">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-coastal-orange/10 rounded-xl flex items-center justify-center">
                    <span className="text-2xl font-heading font-bold text-coastal-orange">
                      $0
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-coastal-navy">
                      Startup Cost
                    </p>
                    <p className="text-sm text-gray-500">No inventory needed</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-coastal-navy/30 rounded-full flex justify-center pt-2">
          <div className="w-1.5 h-3 bg-coastal-navy/50 rounded-full" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
